import { createFocusTrap } from 'focus-trap';
import { gsap } from 'gsap';

const MENU_TRANSITION_DURATION = 0.3;
const SUBMENU_TRANSITION_DURATION = 250;
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const initializeSiteMenu = (header) => {
  const homeLink = header.querySelector('.site-header__home');
  const menuToggle = header.querySelector('[data-site-menu-toggle]');
  const panel = header.querySelector('[data-site-menu-panel]');
  const dialog = header.querySelector('[data-site-menu-dialog]');
  const media = dialog?.querySelector('.site-header__media');
  const submenuTriggers = [
    ...header.querySelectorAll('[data-site-submenu-trigger]'),
  ];

  if (!menuToggle || !panel || !dialog) return;

  const inactiveAriaLabel = menuToggle.dataset.inactiveAriaLabel || 'Open menu';
  const activeAriaLabel = menuToggle.dataset.activeAriaLabel || 'Close menu';

  let isOpen = false;
  const submenuAnimations = new Map();

  const setSubmenuExpanded = (trigger, expanded) => {
    const submenu = document.getElementById(
      trigger.getAttribute('aria-controls') ?? '',
    );

    trigger.setAttribute('aria-expanded', String(expanded));
    if (!submenu) return;

    const wasHidden = submenu.hidden;
    const startHeight = wasHidden ? 0 : submenu.getBoundingClientRect().height;
    const startOpacity = wasHidden
      ? 0
      : Number.parseFloat(window.getComputedStyle(submenu).opacity);

    submenuAnimations.get(submenu)?.cancel();
    submenu.hidden = false;

    const endHeight = expanded ? submenu.scrollHeight : 0;
    if (reducedMotion.matches || startHeight === endHeight) {
      submenu.hidden = !expanded;
      submenuAnimations.delete(submenu);
      return;
    }

    const animation = submenu.animate(
      [
        { height: `${startHeight}px`, opacity: startOpacity },
        { height: `${endHeight}px`, opacity: expanded ? 1 : 0 },
      ],
      {
        duration: SUBMENU_TRANSITION_DURATION,
        easing: 'ease-out',
      },
    );

    submenuAnimations.set(submenu, animation);
    animation.addEventListener('finish', () => {
      if (submenuAnimations.get(submenu) !== animation) return;
      submenu.hidden = !expanded;
      submenuAnimations.delete(submenu);
    });
  };

  const collapseSubmenus = (exception) => {
    submenuTriggers.forEach((trigger) => {
      if (trigger === exception) return;
      if (trigger.getAttribute('aria-expanded') === 'true') {
        setSubmenuExpanded(trigger, false);
      }
    });
  };

  const finishClose = () => {
    panel.hidden = true;
    gsap.set(panel, { clearProps: 'opacity,transform' });
    media?.style.removeProperty('height');
    header.classList.remove('is-menu-closing');
    document.documentElement.classList.remove('has-open-site-menu');
  };

  const setMenuClosed = () => {
    isOpen = false;
    menuToggle.setAttribute('aria-expanded', 'false');
    menuToggle.setAttribute('aria-label', inactiveAriaLabel);
    header.classList.remove('is-menu-open');
    header.classList.add('is-menu-closing');
    collapseSubmenus();
    gsap.killTweensOf(panel);

    if (reducedMotion.matches) {
      finishClose();
      return;
    }

    gsap.to(panel, {
      duration: MENU_TRANSITION_DURATION,
      ease: 'power1.out',
      opacity: 0,
      yPercent: -100,
      onComplete: finishClose,
    });
  };

  const focusTrap = createFocusTrap(header, {
    delayReturnFocus: false,
    fallbackFocus: menuToggle,
    initialFocus: menuToggle,
    isolateSubtrees: true,
    onDeactivate: setMenuClosed,
    setReturnFocus: menuToggle,
  });

  const closeMenu = ({ restoreFocus = true } = {}) => {
    if (!isOpen) return;
    focusTrap.deactivate({ returnFocus: restoreFocus });
  };

  const openMenu = () => {
    if (isOpen) return;

    const isReopening = header.classList.contains('is-menu-closing');
    panel.hidden = false;
    media?.style.removeProperty('height');
    header.classList.remove('is-menu-closing');
    gsap.killTweensOf(panel);

    if (!isReopening) {
      gsap.set(panel, { opacity: 0, yPercent: -100 });
    }

    isOpen = true;
    menuToggle.setAttribute('aria-expanded', 'true');
    menuToggle.setAttribute('aria-label', activeAriaLabel);
    document.documentElement.classList.add('has-open-site-menu');
    focusTrap.activate();
    header.classList.add('is-menu-open');

    if (reducedMotion.matches) {
      gsap.set(panel, { opacity: 1, yPercent: 0 });
    } else {
      gsap.to(panel, {
        duration: MENU_TRANSITION_DURATION,
        ease: 'power1.out',
        opacity: 1,
        yPercent: 0,
      });
    }

    window.requestAnimationFrame(() => {
      if (media && window.getComputedStyle(media).display !== 'none') {
        media.style.height = `${media.getBoundingClientRect().height}px`;
      }
    });
  };

  menuToggle.addEventListener('click', () => {
    if (isOpen) closeMenu();
    else openMenu();
  });

  header.addEventListener('click', (event) => {
    if (!isOpen) return;
    if (
      homeLink?.contains(event.target) ||
      dialog.contains(event.target) ||
      menuToggle.contains(event.target)
    ) {
      return;
    }

    closeMenu();
  });

  submenuTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const willExpand = trigger.getAttribute('aria-expanded') !== 'true';
      collapseSubmenus(willExpand ? trigger : undefined);
      setSubmenuExpanded(trigger, willExpand);
    });
  });
};

export const initializeSiteMenus = () => {
  document.querySelectorAll('[data-site-header]').forEach(initializeSiteMenu);
};
